import React, { useEffect, useReducer } from "react";
import "./style.css";
import makeData from "./makeData";
import Table from "./Table";
import { randomColor, shortId } from "./utils";
import { grey } from "./colors";

function reducer(state, action) {
  switch (action.type) {
    case "add_option_to_column":
      const optionIndex = state.columns.findIndex(
        (column) => column.id === action.columnId
      );
      return {
        ...state,
        skipReset: true,
        columns: [
          ...state.columns.slice(0, optionIndex),
          {
            ...state.columns[optionIndex],
            options: [
              ...state.columns[optionIndex].options,
              { label: action.option, backgroundColor: action.backgroundColor },
            ],
          },
          ...state.columns.slice(optionIndex + 1, state.columns.length),
        ],
      };
    case "add_row":
      return {
        ...state,
        skipReset: true,
        data: [{ isNewAdded: true }, ...state.data],
      };
    case "update_column_type":
      const typeIndex = state.columns.findIndex(
        (column) => column.id === action.columnId
      );
      switch (action.dataType) {
        case "number":
          if (state.columns[typeIndex].dataType === "number") {
            return state;
          } else {
            return {
              ...state,
              columns: [
                ...state.columns.slice(0, typeIndex),
                { ...state.columns[typeIndex], dataType: action.dataType },
                ...state.columns.slice(typeIndex + 1, state.columns.length),
              ],
              data: state.data.map((row) => ({
                ...row,
                [action.columnId]: isNaN(row[action.columnId])
                  ? ""
                  : Number.parseInt(row[action.columnId]),
              })),
            };
          }
        case "select":
          if (state.columns[typeIndex].dataType === "select") {
            return {
              ...state,
              columns: [
                ...state.columns.slice(0, typeIndex),
                { ...state.columns[typeIndex], dataType: action.dataType },
                ...state.columns.slice(typeIndex + 1, state.columns.length),
              ],
              skipReset: true,
            };
          } else {
            let options = [];
            state.data.forEach((row) => {
              if (row[action.columnId]) {
                options.push({
                  label: row[action.columnId],
                  backgroundColor: randomColor(),
                });
              }
            });
            return {
              ...state,
              columns: [
                ...state.columns.slice(0, typeIndex),
                {
                  ...state.columns[typeIndex],
                  dataType: action.dataType,
                  options: [...state.columns[typeIndex].options, ...options],
                },
                ...state.columns.slice(typeIndex + 1, state.columns.length),
              ],
              skipReset: true,
            };
          }
        case "text":
          if (state.columns[typeIndex].dataType === "text") {
            return state;
          } else if (state.columns[typeIndex].dataType === "select") {
            return {
              ...state,
              skipReset: true,
              columns: [
                ...state.columns.slice(0, typeIndex),
                { ...state.columns[typeIndex], dataType: action.dataType },
                ...state.columns.slice(typeIndex + 1, state.columns.length),
              ],
            };
          } else {
            return {
              ...state,
              skipReset: true,
              columns: [
                ...state.columns.slice(0, typeIndex),
                { ...state.columns[typeIndex], dataType: action.dataType },
                ...state.columns.slice(typeIndex + 1, state.columns.length),
              ],
              data: state.data.map((row) => ({
                ...row,
                [action.columnId]: row[action.columnId] + "",
              })),
            };
          }
        default:
          return state;
      }
    case "update_column_header":
      const index = state.columns.findIndex(
        (column) => column.id === action.columnId
      );
      return {
        ...state,
        skipReset: true,
        columns: [
          ...state.columns.slice(0, index),
          { ...state.columns[index], label: action.label },
          ...state.columns.slice(index + 1, state.columns.length),
        ],
      };
    case "update_cell":
      return {
        ...state,
        skipReset: true,
        data: state.data.map((row, index) => {
          if (index === action.rowIndex) {
            return {
              ...state.data[action.rowIndex],
              [action.columnId]: action.value,
            };
          }
          return row;
        }),
      };
    case "select_deselect_all_cell":
      const isAllSelected = state.data.every((row) => row?.isRowSelected === true);
      return {
        ...state,
        skipReset: true,
        data: state.data.map((row) => {   
          return {
            ...row,
            isRowSelected: !isAllSelected,
          };
        }),
      };
    case "add_column_to_left":
      const leftIndex = state.columns.findIndex(
        (column) => column.id === action.columnId
      );
      let leftId = shortId();
      return {
        ...state,
        skipReset: true,
        columns: [
          ...state.columns.slice(0, leftIndex),
          {
            id: leftId,
            label: "Column",
            accessor: leftId,
            dataType: "text",
            created: action.focus && true,
            options: [],
            isNewAdded: true,
          },
          ...state.columns.slice(leftIndex, state.columns.length),
        ],
      };
    case "add_column_to_right":
      const rightIndex = state.columns.findIndex(
        (column) => column.id === action.columnId
      );
      const rightId = shortId();
      return {
        ...state,
        skipReset: true,
        columns: [
          ...state.columns.slice(0, rightIndex + 1),
          {
            id: rightId,
            label: "Column",
            accessor: rightId,
            dataType: "text",
            created: action.focus && true,
            options: [],
            isNewAdded: true,
          },
          ...state.columns.slice(rightIndex + 1, state.columns.length),
        ],
      };
    case "delete_column":
      const deleteIndex = state.columns.findIndex(
        (column) => column.id === action.columnId
      );
      return {
        ...state,
        skipReset: true,
        columns: [
          ...state.columns.slice(0, deleteIndex),
          ...state.columns.slice(deleteIndex + 1, state.columns.length),
        ],
      };
    case "enable_reset":
      return {
        ...state,
        skipReset: false,
      };
    case "UPDATE_DATA":
      return { ...action.payload, isError: false };
    case "SET_ERROR":
      return {
        ...state,
        isError: action.payload,
      };
    default:
      return state;
  }
}

function EditableTable({ isMultiSelect, data, onChangeData }) {
  const API_DATA = {
    data: {
      columns: [
        {
          id: "age",
          label: "Age",
          accessor: "age",
          options: [],
        },
        {
          id: "dob",
          label: "Dob",
          accessor: "dob",
          options: [],
        },
        {
          id: "email",
          label: "Email",
          accessor: "email",
          options: [],
        },
        {
          id: "resume",
          label: "Resume",
          accessor: "resume",
          options: [],
        },
        {
          id: "phone_no",
          label: "Phone_no",
          accessor: "phone_no",
          options: [],
        },
        {
          id: "last_name",
          label: "Last_name",
          accessor: "last_name",
          options: [],
        },
        {
          id: "portfolio",
          label: "Portfolio",
          accessor: "portfolio",
          options: [],
        },
        {
          id: "first_name",
          label: "First_name",
          accessor: "first_name",
          options: [],
        },
        {
          id: "url_test",
          label: "Url_test",
          accessor: "url_test",
          options: [],
        },
      ],
      data: [],
    },
  };
  const tableFormattedData = {
    columns: [
      ...(data?.columns && Array.isArray(data?.columns)
        ? [
            ...(isMultiSelect
              ? [
                  {
                    id: "isRowSelected",
                    width: 20,
                    label: "o",
                    disableResizing: true,
                    key: "isRowSelected",
                    dataType: "checkbox-select",
                  },
                ]
              : []),
            ...data?.columns,
          ].sort((a, b) => a?.order - b?.order)
        : API_DATA.data.columns),
      {
        id: 999999,
        width: 20,
        label: "+",
        disableResizing: true,
        dataType: "null",
      },
    ],
    data: (data?.data ?? [])?.map((_row) => ({ ..._row, ID: _row.id })),
    skipReset: false,
  }; //makeData(10);
  const [state, dispatch] = useReducer(reducer, tableFormattedData);

  useEffect(() => {
    dispatch({ type: "enable_reset" });
    const isColumnChanged =
      JSON.stringify(tableFormattedData.columns) !=
      JSON.stringify(state.columns);
    const isRowChanged =
      JSON.stringify(tableFormattedData.data) != JSON.stringify(state.data);
    if (isColumnChanged || isRowChanged) {
      const isRequiredFieldsNotFilled = state.data?.some((objData) => {
        let isValid = false;
        if (objData?.isNewAdded) {
          state.columns?.map((column) => {
            // if (column?.required && (!objData[column?.accessor] || objData[column?.accessor] == "<br>")) {
            //   isValid = true;
            // }
          });
        }
        return isValid;
      });
      if (isRequiredFieldsNotFilled || state?.isError) {
        return;
      }
      const data = {
        columns: state.columns?.filter(
          (column) => !(column?.id == 999999 && column?.dataType == "null")
        ),
        data: (state.data ?? [])?.map((_row) => ({ ..._row, ID: undefined })),
      };
      if (onChangeData) {
        onChangeData(data);
      }
    }
  }, [
    JSON.stringify(state.data),
    JSON.stringify(state.columns),
    state?.isError,
  ]);

  useEffect(() => {
    dispatch({ type: "UPDATE_DATA", payload: tableFormattedData });
  }, [JSON.stringify(tableFormattedData)]);

  return (
    <Table
      columns={state.columns}
      data={state.data}
      dispatch={dispatch}
      skipReset={state.skipReset}
      isError={state?.isError}
      isMultiSelect={isMultiSelect}
    />
  );
}

export default EditableTable;
